// WHAT: Component displaying roadmap items distribution by status with progress bars
// WHY:  Provides visual representation of item status distribution for analytics dashboard
// DOMAIN: Admin → Roadmap → Analytics
// DEPENDENCIES: Card, Badge, ProgressBar components, Lucide icons
// RISKS:
//   - NOTE: Division by zero if total is 0 - percentage calculation will be NaN
//   - NOTE: Dynamic Tailwind classes may not work with JIT compiler
//   - NOTE: Status string replacement (replace('_', ' ')) only replaces first underscore

import {
  LucidePieChart,
  LucideCheckCircle,
  LucideClock,
  LucideFolderOpen,
  LucideAlertTriangle,
  LucideActivity,
} from "lucide-react";
import React from "react";

import { Card, Badge, ProgressBar } from "@/components/ui";

/**
 * Props for StatusDistribution component
 */
interface StatusDistributionProperties {
  /** Object mapping status names to item counts */
  byStatus: Record<string, number>;
  /** Total number of items for percentage calculation */
  total: number;
}

/**
 * WHAT:
 *   Returns configuration (icon, variant, color) for a given status.
 *
 * WHY:
 *   Centralizes status-to-visual-configuration mapping for consistency.
 *
 * BEHAVIOR:
 *   - Maps known statuses to specific icons and colors
 *   - Returns default configuration for unknown statuses
 *
 * RISKS:
 *   - NOTE: Unknown statuses get default config - may not be visually distinct
 *
 * @param status - Status string to get configuration for
 *
 * @returns {object} Configuration object with icon, variant, and color
 *
 */
const getStatusConfig = (status: string) => {
  switch (status) {
    case "done": {
      return {
        color: "green-500",
        icon: LucideCheckCircle,
        variant: "success" as const,
      };
    }
    case "in_progress": {
      return {
        color: "tracq-bright-blue",
        icon: LucideClock,
        variant: "primary" as const,
      };
    }
    case "open": {
      return {
        color: "blue-500",
        icon: LucideFolderOpen,
        variant: "info" as const,
      };
    }
    case "rejected": {
      return {
        color: "red-500",
        icon: LucideAlertTriangle,
        variant: "destructive" as const,
      };
    }
    default: {
      return {
        color: "slate-500",
        icon: LucideActivity,
        variant: "default" as const,
      };
    }
  }
};

/**
 * WHAT:
 *   Component that displays a breakdown of roadmap items by status with
 *   progress bars showing the percentage distribution.
 *
 * WHY:
 *   Provides visual insight into how roadmap items are distributed across
 *   different statuses, helping identify workflow bottlenecks.
 *
 * PROPS:
 *   - byStatus: Record<string, number> — Status name to item count mapping
 *   - total: number — Total item count for percentage calculation
 *
 * BEHAVIOR:
 *   - Maps status strings to icons, variants, and colors
 *   - Calculates percentage for each status (count / total * 100)
 *   - Displays progress bar and percentage text
 *   - Handles unknown statuses with default configuration
 *
 * RISKS:
 *   - NOTE: Division by zero if total is 0 - add validation or default value
 *   - NOTE: Dynamic Tailwind classes may not be purged correctly - use static classes
 *   - NOTE: Status string replacement only handles first underscore - "in_progress" becomes "in progress" but "very_long_status" stays
 *
 * @param root0
 *
 * @param root0.byStatus
 *
 * @param root0.total
 *
 * @returns {JSX.Element} Die gerenderte StatusDistribution-Komponente
 *
 */
export const StatusDistribution: React.FC<StatusDistributionProperties> = ({ byStatus, total }) => {
  return (
    <Card padding="lg" variant="default">
      <div className="flex items-center gap-2 mb-6">
        <LucidePieChart className="w-5 h-5 text-tracq-bright-blue" />
        <h3 className="text-lg font-medium text-white">Status Distribution</h3>
      </div>

      <div className="space-y-4">
        {Object.entries(byStatus).map(([status, count]) => {
          const statusConfig = getStatusConfig(status);
          const percentage = (count / total) * 100;
          const IconComponent = statusConfig.icon;

          return (
            <div className="space-y-2" key={status}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <IconComponent className={`w-4 h-4 text-${statusConfig.color}`} />
                  <Badge size="sm" style="subtle" variant={statusConfig.variant}>
                    {status.replace("_", " ")}
                  </Badge>
                </div>
                <span className="text-sm text-slate-400 font-medium">
                  {count} ({percentage.toFixed(1)}%)
                </span>
              </div>
              <ProgressBar
                max={100}
                showLabel={false}
                size="sm"
                state={(() => {
                  if (statusConfig.variant === "success") {
                    return "success";
                  }
                  if (statusConfig.variant === "destructive") {
                    return "error";
                  }
                  return "default";
                })()}
                value={percentage}
              />
            </div>
          );
        })}
      </div>
    </Card>
  );
};
